import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Checkbox } from '../../ui/checkbox'
import { Label } from '../../ui/label'

function SkillsEditor({ skills = [], onChange, readOnly = false }) {
  const [newSkillName, setNewSkillName] = useState('')
  const inputRef = useRef(null)

  function handleAddSkill() {
    if (!newSkillName.trim()) return

    const newSkill = {
      tempId: `temp-${Date.now()}`,
      name: newSkillName.trim(),
      shortLabel: String(skills.length + 1),
      ordinal: skills.length + 1,
      optional: false
    }

    onChange([...skills, newSkill])
    setNewSkillName('')
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  function handleRemoveSkill(index) {
    const updated = skills.filter((_, i) => i !== index)
    onChange(updated.map((skill, i) => ({ ...skill, ordinal: i + 1, shortLabel: skill.shortLabel || String(i + 1) })))
  }

  function handleUpdateSkill(index, field, value) {
    const updated = skills.map((skill, i) => {
      if (i === index) {
        return { ...skill, [field]: value }
      }
      return skill
    })
    onChange(updated)
  }

  function handleMoveSkill(fromIndex, toIndex) {
    if (toIndex < 0 || toIndex >= skills.length) return

    const updated = [...skills]
    const [removed] = updated.splice(fromIndex, 1)
    updated.splice(toIndex, 0, removed)

    onChange(updated.map((skill, i) => ({ ...skill, ordinal: i + 1, shortLabel: String(i + 1) })))
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddSkill()
    }
  }

  if (readOnly) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">Skills</Label>
        {skills.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No skills defined</p>
        ) : (
          <div className="space-y-1 border border-gray-200 rounded-lg p-2 bg-gray-50">
            {skills.map((skill, index) => (
              <div key={skill.id || skill.tempId || index} className="flex items-center gap-2 py-1 text-sm">
                <span className="w-6 h-6 flex items-center justify-center bg-purple-100 text-purple-700 rounded text-xs font-medium">
                  {skill.shortLabel || index + 1}
                </span>
                <span className="text-gray-700 flex-1">{skill.name}</span>
                {skill.optional && (
                  <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Optional</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Skills</Label>

      {skills.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {skills.map((skill, index) => (
            <div
              key={skill.id || skill.tempId || index}
              className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleMoveSkill(index, index - 1)}
                  disabled={index === 0}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                  title="Move up"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveSkill(index, index + 1)}
                  disabled={index === skills.length - 1}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                  title="Move down"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              <span className="w-6 h-6 flex items-center justify-center bg-purple-100 text-purple-700 rounded text-xs font-medium flex-shrink-0">
                {index + 1}
              </span>

              <Input
                type="text"
                value={skill.name}
                onChange={(e) => handleUpdateSkill(index, 'name', e.target.value)}
                className="flex-1 h-8 text-sm"
                placeholder="Skill name"
              />

              <div className="flex items-center gap-1">
                <Checkbox
                  id={`optional-${skill.id || skill.tempId || index}`}
                  checked={skill.optional || false}
                  onCheckedChange={(checked) => handleUpdateSkill(index, 'optional', checked)}
                />
                <Label
                  htmlFor={`optional-${skill.id || skill.tempId || index}`}
                  className="text-xs text-gray-500 cursor-pointer"
                >
                  Optional
                </Label>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveSkill(index)}
                className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          type="text"
          value={newSkillName}
          onChange={(e) => setNewSkillName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a skill..."
          className="flex-1 h-9"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddSkill}
          disabled={!newSkillName.trim()}
          className="h-9"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      <p className="text-xs text-gray-500">
        Press Enter to quickly add skills. Use the arrows to reorder.
      </p>
    </div>
  )
}

SkillsEditor.propTypes = {
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tempId: PropTypes.string,
      name: PropTypes.string.isRequired,
      shortLabel: PropTypes.string,
      ordinal: PropTypes.number,
      optional: PropTypes.bool
    })
  ),
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
}

export default SkillsEditor
